import React, { useState, useEffect } from 'react';
import { DevicePreview } from './DevicePreview';
import { VariableManager } from './VariableManager';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertCircle, Check, Info, Shield, Smartphone } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

interface EmailSettingsProps {
  htmlContent: string;
  onVariableInsert: (variable: string) => void;
}

export const EmailSettings: React.FC<EmailSettingsProps> = ({ 
  htmlContent, 
  onVariableInsert 
}) => {
  const [spamScore, setSpamScore] = useState(0);
  const [spamChecks, setSpamChecks] = useState<{
    title: string;
    result: 'pass' | 'warning' | 'fail';
    impact: number;
    description: string;
  }[]>([]);
  
  // Simulate spam score assessment based on the htmlContent
  useEffect(() => {
    if (!htmlContent) return;
    
    // This would be a more complex and accurate analysis in a real implementation
    const checks = [
      {
        title: 'HTML to Text Ratio',
        result: htmlContent.includes('<table') && !htmlContent.includes('{{') 
          ? 'warning' 
          : 'pass',
        impact: 3,
        description: 'Too much HTML compared to text content can trigger spam filters.'
      },
      {
        title: 'Suspicious Words',
        result: /free|cash|prize|winner|money|offer|limited|buy|discount/gi.test(htmlContent) 
          ? 'warning' 
          : 'pass',
        impact: 5,
        description: 'Common spam trigger words were detected.'
      },
      {
        title: 'Image to Text Ratio',
        result: (htmlContent.match(/<img/g) || []).length > 3 ? 'warning' : 'pass',
        impact: 2,
        description: 'Too many images compared to text content.'
      },
      {
        title: 'Links to Text Ratio',
        result: (htmlContent.match(/<a /g) || []).length > 5 ? 'warning' : 'pass',
        impact: 4,
        description: 'Too many links can trigger spam filters.'
      },
      {
        title: 'ALL CAPS Text',
        result: />[A-Z]{10,}</.test(htmlContent) ? 'fail' : 'pass',
        impact: 8,
        description: 'Using ALL CAPS for large portions of text is a spam indicator.'
      },
      {
        title: 'Email Client Compatibility',
        result: 'pass',
        impact: 0,
        description: 'Your email should display correctly across most email clients.'
      }
    ];
    
    // Calculate spam score based on checks
    const calculatedScore = checks.reduce((score, check) => {
      if (check.result === 'warning') return score + (check.impact * 0.5);
      if (check.result === 'fail') return score + check.impact;
      return score;
    }, 0);
    
    setSpamChecks(checks);
    setSpamScore(Math.min(calculatedScore, 100));
  }, [htmlContent]);
  
  const getScoreColor = () => {
    if (spamScore < 30) return 'bg-green-500';
    if (spamScore < 70) return 'bg-amber-500';
    return 'bg-red-500';
  };
  
  const getScoreLabel = () => {
    if (spamScore < 30) return 'Good';
    if (spamScore < 70) return 'Needs Improvement';
    return 'High Risk';
  };

  return (
    <Tabs defaultValue="preview">
      <TabsList className="mb-4">
        <TabsTrigger value="preview" className="flex items-center gap-1">
          <Smartphone className="h-4 w-4" />
          Device Preview
        </TabsTrigger>
        <TabsTrigger value="variables" className="flex items-center gap-1">
          <Info className="h-4 w-4" />
          Variables
        </TabsTrigger>
        <TabsTrigger value="spam" className="flex items-center gap-1">
          <Shield className="h-4 w-4" />
          Spam Analysis
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="preview" className="mt-0">
        <DevicePreview>
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </DevicePreview>
      </TabsContent>
      
      <TabsContent value="variables" className="mt-0">
        <VariableManager onInsertVariable={onVariableInsert} />
      </TabsContent>
      
      <TabsContent value="spam" className="mt-0">
        <div className="space-y-6">
          <Card className="p-4">
            <div className="mb-4">
              <div className="flex justify-between mb-2">
                <h3 className="font-medium">Spam Score</h3>
                <div className={`text-sm font-medium ${
                  spamScore < 30 ? 'text-green-600' : 
                  spamScore < 70 ? 'text-amber-600' : 
                  'text-red-600'
                }`}>
                  {getScoreLabel()} ({spamScore}/100)
                </div>
              </div>
              <Progress value={spamScore} className={getScoreColor()} />
            </div>
            
            <p className="text-sm text-muted-foreground">
              This score estimates how likely your email is to be flagged as spam. Lower scores are better.
            </p>
          </Card>
          
          <div>
            <h3 className="font-medium mb-4">Analysis Details</h3>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {spamChecks.map((check, index) => (
                  <Card key={index} className="p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex gap-2">
                        {check.result === 'pass' ? (
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : check.result === 'warning' ? (
                          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <h4 className="font-medium text-sm">{check.title}</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">{check.description}</p>
                        </div>
                      </div>
                      {check.result !== 'pass' && (
                        <div className={`text-xs px-2 py-0.5 rounded font-medium ${
                          check.result === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {check.result === 'warning' ? 'Warning' : 'Failed'}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>
            
            <Button 
              variant="outline" 
              className="w-full mt-4"
              disabled
            >
              Get Detailed Analysis
            </Button>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}; 